// ArcLengthRightTriangleDiagram.js
import React from "react";
import { InlineMath } from "react-katex";

export default function ArcLengthRightTriangleDiagram({
  size = 280,
  circleR = 110,
  givenAngleDeg = 40, // wartość kąta podanego w zadaniu (etykieta przy A w tym diagramie)
  showCentral = false, // tryb wyjaśnienia
  centralAngleDeg, // kąt środkowy (w wyjaśnieniu)
  radiusLabel = "r", // podpis promienia (np. "30")
  showBetaAtB = false, // czy pokazywać β przy B (w wyjaśnieniu)
  betaLabel = "β", // "β" albo np. "36°"
}) {
  const cx = size / 2;
  const cy = size / 2;

  const COLORS = {
    base: "currentColor",
    accent: "#f59e0b",
    arc: "#7c3aed",
  };

  // --- helpery do kątów ---
  const normAngle = (a) => {
    let x = a;
    while (x <= -Math.PI) x += 2 * Math.PI;
    while (x > Math.PI) x -= 2 * Math.PI;
    return x;
  };

  // „bezpieczna” dwusieczna kierunków (najkrótsza droga na okręgu)
  const midAngle = (a, b) => {
    const aa = normAngle(a);
    const d = normAngle(normAngle(b) - aa);
    return normAngle(aa + d / 2);
  };

  const clamp = (v, a, b) => Math.max(a, Math.min(b, v));

  // --- Punkty na okręgu ---
  const A = { x: cx - circleR, y: cy };
  const B = { x: cx + circleR, y: cy };
  const S = { x: cx, y: cy };

  // Punkt C: kąt środkowy ∠ASC = 2*givenAngleDeg (jak w Twojej poprzedniej geometrii)
  const phi = (2 * givenAngleDeg * Math.PI) / 180;
  const C = {
    x: cx + circleR * Math.cos(phi),
    y: cy - circleR * Math.sin(phi),
  };

  // --- łuk AC (wyróżniony) ---
  const startArc = A;
  const endArc = C;
  const largeArcFlag = 0;
  const sweepFlag = 1;

  // --- Kąt środkowy przy S między SA i SC ---
  const centralArcR = 28;
  const dirSC = Math.atan2(C.y - S.y, C.x - S.x);
  const dirSA = Math.atan2(A.y - S.y, A.x - S.x);
  const midDirS = midAngle(dirSA, dirSC);

  const centralArcStart = {
    x: S.x + centralArcR * Math.cos(dirSA),
    y: S.y + centralArcR * Math.sin(dirSA),
  };
  const centralArcEnd = {
    x: S.x + centralArcR * Math.cos(dirSC),
    y: S.y + centralArcR * Math.sin(dirSC),
  };

  const centralLabelR = centralArcR + 18;
  const centralLabelPos = {
    x: S.x + centralLabelR * Math.cos(midDirS),
    y: S.y + centralLabelR * Math.sin(midDirS) - 2,
  };

  // --- β przy wierzchołku B (między BA i BC) ---
  const dirBA = Math.atan2(A.y - B.y, A.x - B.x);
  const dirBC = Math.atan2(C.y - B.y, C.x - B.x);
  const midDirB = midAngle(dirBA, dirBC);

  const betaArcR = 23;
  const betaLabelR = 36;
  const betaArcStart = {
    x: B.x + betaArcR * Math.cos(dirBA),
    y: B.y + betaArcR * Math.sin(dirBA),
  };
  const betaArcEnd = {
    x: B.x + betaArcR * Math.cos(dirBC),
    y: B.y + betaArcR * Math.sin(dirBC),
  };

  // widełki co 10° (start od 40°), żeby przy większych kątach przesuwać bardziej w lewo
  const bucketB = clamp(Math.floor((givenAngleDeg - 40) / 10), 0, 6);

  // przesunięcie w lewo: stałe + rosnące co 10°
  const baseLeftShift = 15; // zawsze trochę w lewo
  const shiftPerBucketB = 4; // dodatkowo co 10°
  const xShiftB = baseLeftShift + bucketB * shiftPerBucketB;

  const betaLabelPos = {
    x: B.x + betaLabelR * Math.cos(midDirB) - xShiftB,
    y: B.y + betaLabelR * Math.sin(midDirB) - 2,
  };

  // --- podpisy punktów na zewnątrz ---
  const placeOutside = (pt, offset = 14) => {
    const vx = pt.x - cx;
    const vy = pt.y - cy;
    const len = Math.hypot(vx, vy) || 1;
    const ux = vx / len;
    const uy = vy / len;
    const x = pt.x + offset * ux;
    const y = pt.y + offset * uy;

    let anchor = "middle";
    if (ux > 0.35) anchor = "start";
    else if (ux < -0.35) anchor = "end";

    return { x, y, anchor };
  };

  const Apos = placeOutside(A);
  const Bpos = placeOutside(B);
  const Cpos = placeOutside(C);
  const Spos = { x: S.x, y: S.y - 10, anchor: "middle" };

  const LatexLabel = ({
    x,
    y,
    math,
    color = COLORS.base,
    width = 42,
    height = 24,
    anchor = "middle",
  }) => {
    const boxX = anchor === "start" ? x : anchor === "end" ? x - width : x - width / 2;
    const textAlign = anchor === "start" ? "left" : anchor === "end" ? "right" : "center";

    return (
      <foreignObject x={boxX} y={y - height / 2} width={width} height={height}>
        <div style={{ color, fontSize: 14, textAlign, lineHeight: 1.2 }}>
          <InlineMath math={math} />
        </div>
      </foreignObject>
    );
  };

  const toLatex = (value) => String(value).replace(/°/g, "^{\\circ}");

  const angleAArcR = 26;
  const dirAB = Math.atan2(B.y - A.y, B.x - A.x);
  const dirAC = Math.atan2(C.y - A.y, C.x - A.x);
  const midDirA = midAngle(dirAB, dirAC);
  const smallAngleBoost = clamp((40 - givenAngleDeg) / 25, 0, 1);
  const largeAnglePull = clamp((givenAngleDeg - 50) / 25, 0, 1);
  // Przy małych kątach podpis przesuwamy w prawo i w dół, żeby nie wisiał
  // wysoko nad prawie poziomym bokiem. Przy dużych kątach zbliżamy go do A.
  const angleALabelR = 42 + smallAngleBoost * 31 - largeAnglePull * 8;
  const angleALabelSideShift = 4 + smallAngleBoost * 8;
  // Dolne przesunięcie jest celowo delikatne: przy około 25–30° sam podpis
  // ma pozostać wyraźnie nad średnicą, a nie "siadać" na niej.
  const angleALabelVerticalShift = smallAngleBoost * 7;
  const angleAText = {
    x: A.x + angleALabelR * Math.cos(midDirA) + angleALabelSideShift,
    y: A.y + angleALabelR * Math.sin(midDirA) + angleALabelVerticalShift - 2,
  };

  const angleAArcStart = {
    x: A.x + angleAArcR * Math.cos(dirAB),
    y: A.y + angleAArcR * Math.sin(dirAB),
  };
  const angleAArcEnd = {
    x: A.x + angleAArcR * Math.cos(dirAC),
    y: A.y + angleAArcR * Math.sin(dirAC),
  };

  // --- Promień SB + podpis promienia (przesunięty, żeby nie nachodził na kąt środkowy) ---
  const radiusEnd = B;
  const tRad = 0.5;
  const rx0 = S.x + tRad * (radiusEnd.x - S.x);
  const ry0 = S.y + tRad * (radiusEnd.y - S.y);

  const vxR = radiusEnd.x - S.x;
  const vyR = radiusEnd.y - S.y;
  const vlenR = Math.hypot(vxR, vyR) || 1;
  const nxR = -vyR / vlenR;
  const nyR = vxR / vlenR;

  const radiusLabelPos = {
    x: rx0 + nxR * 16,
    y: ry0 + nyR * 16 + 2,
  };

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      {/* okrąg */}
      <circle
        cx={cx}
        cy={cy}
        r={circleR}
        fill="none"
        stroke={COLORS.base}
        strokeWidth="2"
      />

      {/* średnica AB */}
      <line
        x1={A.x}
        y1={A.y}
        x2={B.x}
        y2={B.y}
        stroke={COLORS.base}
        strokeWidth="2.4"
      />

      {/* promień SB */}
      <line
        x1={S.x}
        y1={S.y}
        x2={radiusEnd.x}
        y2={radiusEnd.y}
        stroke={COLORS.accent}
        strokeWidth="3"
      />

      {/* podpis promienia */}
      <LatexLabel
        x={radiusLabelPos.x}
        y={radiusLabelPos.y}
        math={toLatex(radiusLabel)}
        color={COLORS.accent}
      />

      {/* trójkąt ABC */}
      <line
        x1={A.x}
        y1={A.y}
        x2={C.x}
        y2={C.y}
        stroke={COLORS.base}
        strokeWidth="2.4"
      />
      <line
        x1={B.x}
        y1={B.y}
        x2={C.x}
        y2={C.y}
        stroke={COLORS.base}
        strokeWidth="2.4"
      />

      {/* łuk AC */}
      <path
        d={`M ${startArc.x} ${startArc.y}
           A ${circleR} ${circleR} 0 ${largeArcFlag} ${sweepFlag}
             ${endArc.x} ${endArc.y}`}
        fill="none"
        stroke={COLORS.arc}
        strokeWidth="3"
      />

      {/* podpis kąta przy A (podany w treści) */}
      <path
        d={`M ${angleAArcStart.x} ${angleAArcStart.y}
           A ${angleAArcR} ${angleAArcR} 0 0 0
             ${angleAArcEnd.x} ${angleAArcEnd.y}`}
        fill="none"
        stroke={COLORS.accent}
        strokeWidth="2.4"
      />
      <LatexLabel
        x={angleAText.x}
        y={angleAText.y}
        math={`${givenAngleDeg}^{\\circ}`}
        color={COLORS.accent}
      />

      {/* tryb wyjaśnień */}
      {showCentral && typeof centralAngleDeg === "number" && (
        <>
          {/* SC (fiolet, jak łuk) */}
          <line
            x1={S.x}
            y1={S.y}
            x2={C.x}
            y2={C.y}
            stroke={COLORS.arc}
            strokeWidth="2.6"
            opacity="0.95"
          />

          {/* łuk kąta środkowego */}
          <path
            d={`M ${centralArcStart.x} ${centralArcStart.y}
               A ${centralArcR} ${centralArcR} 0 0 1
                 ${centralArcEnd.x} ${centralArcEnd.y}`}
            fill="none"
            stroke={COLORS.accent}
            strokeWidth="2.5"
          />

          {/* etykieta kąta środkowego */}
          <LatexLabel
            x={centralLabelPos.x}
            y={centralLabelPos.y}
            math={`${centralAngleDeg}^{\\circ}`}
            color={COLORS.accent}
          />

          {/* β przy B */}
          {showBetaAtB && (
            <>
              <path
                d={`M ${betaArcStart.x} ${betaArcStart.y}
                   A ${betaArcR} ${betaArcR} 0 0 1
                     ${betaArcEnd.x} ${betaArcEnd.y}`}
                fill="none"
                stroke={COLORS.accent}
                strokeWidth="2.4"
              />
              <LatexLabel
                x={betaLabelPos.x}
                y={betaLabelPos.y}
                math={toLatex(betaLabel)}
                color={COLORS.accent}
              />
            </>
          )}
        </>
      )}

      {/* punkty */}
      <circle cx={A.x} cy={A.y} r="4" fill={COLORS.base} />
      <circle cx={B.x} cy={B.y} r="4" fill={COLORS.base} />
      <circle cx={C.x} cy={C.y} r="4" fill={COLORS.base} />
      <circle cx={S.x} cy={S.y} r="3.2" fill={COLORS.base} />

      {/* etykiety punktów */}
      <LatexLabel
        x={Apos.x}
        y={Apos.y}
        math="A"
        anchor={Apos.anchor}
      />
      <LatexLabel
        x={Bpos.x}
        y={Bpos.y}
        math="B"
        anchor={Bpos.anchor}
      />
      <LatexLabel
        x={Cpos.x}
        y={Cpos.y}
        math="C"
        anchor={Cpos.anchor}
      />
      <LatexLabel
        x={Spos.x}
        y={Spos.y}
        math="S"
        anchor={Spos.anchor}
      />
    </svg>
  );
}
